import { useEffect } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import type { CreateWebinarInput, UpdateWebinarInput } from '@proref/schemas';
import type { Webinar, WebinarType, WebinarPlatform } from '@proref/types';
import { useCreateWebinar, useUpdateWebinar } from '../../hooks/use-webinars';
import { PLATFORM_OPTIONS, detectPlatform, speakersOf } from '../../lib/webinar-platform';
import { Modal } from '../../components/shared/Modal';
import { Field } from '../../components/shared/Field';
import { Input } from '../../components/shared/Input';
import { Button } from '../../components/shared/Button';
import { Spinner } from '../../components/shared/Spinner';
import { Icon } from '../../components/shared/Icons';
import { notifyError, notifySuccess } from '../../lib/toast';

// More than this stops being a webinar and starts being a conference — and matches the cap the API
// schema enforces, so the form can't compose a body the server will reject.
const MAX_SPEAKERS = 6;

interface FormValues {
  title: string;
  description: string;
  type: WebinarType;
  // <input type="datetime-local"> value — a local wall-clock string with no timezone.
  scheduled_at: string;
  // Optional numbers; '' from an empty input is coerced to undefined via setValueAs.
  duration_min?: number;
  capacity?: number;
  // '' = not decided yet (the host knows the date before they know the room).
  platform: WebinarPlatform | '';
  join_url: string;
  speakers: { name: string; title: string }[];
}

interface WebinarFormDialogProps {
  open: boolean;
  onClose: () => void;
  // When the host is the ProRef team (super_admin), the webinar is GENERAL (all sports + federations).
  // Purely a copy hint here — the server derives the real scope from the JWT, never this flag.
  isGeneral?: boolean;
  // Absent → create. Present → edit that webinar (same fields, PATCHed instead of POSTed).
  webinar?: Webinar | null;
}

const EMPTY: FormValues = {
  title: '', description: '', type: 'group', scheduled_at: '',
  platform: '', join_url: '', speakers: [],
};

// An ISO timestamp as the local wall-clock string <input type="datetime-local"> expects. Going
// through toISOString() here would shift the displayed time by the viewer's UTC offset — the host
// must see back exactly the local time they typed.
function toLocalInputValue(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function toFormValues(w: Webinar): FormValues {
  return {
    title: w.title,
    description: w.description ?? '',
    type: w.type,
    scheduled_at: toLocalInputValue(w.scheduled_at),
    ...(w.duration_min !== null ? { duration_min: w.duration_min } : {}),
    ...(w.capacity !== null ? { capacity: w.capacity } : {}),
    platform: w.platform ?? '',
    // Only an attendee or a host is given the link — this dialog only ever opens for a host, so
    // it's populated; a null here would mean the viewer couldn't have opened the form anyway.
    join_url: w.join_url ?? '',
    speakers: speakersOf(w).map((s) => ({ name: s.name, title: s.title ?? '' })),
  };
}

// Schedule / edit a webinar. One dialog for both because the two forms are the same form: the only
// differences are which mutation runs and the copy. Editing exists mainly because the meeting link
// rarely exists when the session is booked — a host sets the date now and pastes the Teams/Zoom
// room days later.
//
// The host's scope (own federation vs general) is JWT-derived server-side, so the form only collects
// the human-authored fields.
export function WebinarFormDialog({ open, onClose, isGeneral = false, webinar }: WebinarFormDialogProps) {
  const create = useCreateWebinar();
  const update = useUpdateWebinar();
  const isEdit = Boolean(webinar);
  const pending = create.isPending || update.isPending;

  const { register, handleSubmit, reset, control, setValue, getValues, formState: { errors } } =
    useForm<FormValues>({ defaultValues: EMPTY });
  const speakers = useFieldArray({ control, name: 'speakers' });

  // Re-seed the form whenever the dialog opens (or is pointed at a different webinar) — a single
  // mounted dialog is reused for every row on the page, so without this the second Edit click would
  // show the first webinar's values.
  useEffect(() => {
    if (open) reset(webinar ? toFormValues(webinar) : EMPTY);
  }, [open, webinar, reset]);

  function closeAndReset() {
    reset(EMPTY);
    onClose();
  }

  // Pasting a recognised meeting link picks the platform for the host. Only fills a BLANK choice —
  // an explicit selection is never overwritten, in case a federation routes a Teams link through
  // its own domain and has deliberately marked it Other.
  function onJoinUrlBlur(value: string) {
    if (getValues('platform')) return;
    const detected = detectPlatform(value);
    if (detected) setValue('platform', detected, { shouldDirty: true });
  }

  const onSubmit = handleSubmit(async (values) => {
    // Empty rows are how an unfinished "+ Add speaker" click looks — drop them rather than
    // rejecting the whole submit over a row the host simply didn't fill in.
    const cleanedSpeakers = values.speakers
      .map((s) => ({ name: s.name.trim(), title: s.title.trim() }))
      .filter((s) => s.name.length > 0)
      .map((s) => (s.title ? { name: s.name, title: s.title } : { name: s.name }));

    try {
      if (webinar) {
        // PATCH: send every field, using null to CLEAR the optional ones the host emptied out.
        const patch: UpdateWebinarInput = {
          title: values.title,
          description: values.description.trim() || null,
          type: values.type,
          scheduled_at: new Date(values.scheduled_at).toISOString(),
          duration_min: values.duration_min ?? null,
          capacity: values.capacity ?? null,
          platform: values.platform || null,
          join_url: values.join_url.trim() || null,
          speakers: cleanedSpeakers,
        };
        await update.mutateAsync({ id: webinar.id, input: patch });
        notifySuccess('Webinar updated');
      } else {
        const payload: CreateWebinarInput = {
          title: values.title,
          type: values.type,
          scheduled_at: new Date(values.scheduled_at).toISOString(),
          // Only send the optional fields when the host actually filled them in.
          ...(values.description.trim() ? { description: values.description.trim() } : {}),
          ...(values.duration_min ? { duration_min: values.duration_min } : {}),
          ...(values.capacity ? { capacity: values.capacity } : {}),
          ...(values.platform ? { platform: values.platform } : {}),
          ...(values.join_url.trim() ? { join_url: values.join_url.trim() } : {}),
          ...(cleanedSpeakers.length > 0 ? { speakers: cleanedSpeakers } : {}),
        };
        await create.mutateAsync(payload);
        notifySuccess('Webinar scheduled');
      }
      closeAndReset();
    } catch (err) {
      notifyError(err, isEdit ? 'Could not save the webinar' : 'Could not schedule the webinar');
    }
  });

  return (
    <Modal
      open={open}
      onClose={pending ? () => {} : closeAndReset}
      title={isEdit ? 'Edit webinar' : 'Schedule a webinar'}
      sub={
        isEdit
          ? 'Change the details, or add the meeting link now that the room exists.'
          : isGeneral
            ? 'A general webinar — every referee on the platform, in any sport, can reserve a seat.'
            : 'Your referees will see it in their upcoming list and can reserve a seat.'
      }
      footer={
        <>
          <Button onClick={closeAndReset} disabled={pending}>
            Cancel
          </Button>
          <Button form="webinar-form" type="submit" variant="accent" disabled={pending}>
            {pending ? (
              <>
                <Spinner className="w-3.5 h-3.5" /> {isEdit ? 'Saving…' : 'Scheduling…'}
              </>
            ) : isEdit ? (
              'Save changes'
            ) : (
              <>
                Schedule <Icon.plus className="w-3.5 h-3.5" />
              </>
            )}
          </Button>
        </>
      }
    >
      <form id="webinar-form" onSubmit={onSubmit} className="flex flex-col gap-3.5" noValidate>
        <Field label="Title" htmlFor="webinar-title" error={errors.title?.message}>
          <Input
            id="webinar-title"
            placeholder="Match simulation: 7-metre & clear-chance calls"
            {...register('title', { required: 'Title is required', maxLength: 200 })}
          />
        </Field>

        <Field label="Description" htmlFor="webinar-description" error={errors.description?.message}>
          <textarea
            id="webinar-description"
            rows={3}
            placeholder="What the session covers, who it's for…"
            className="w-full resize-y rounded-sm border border-line-2 bg-surface px-3 py-2 text-sm text-ink outline-none focus:border-ink-3"
            {...register('description', { maxLength: 2000 })}
          />
        </Field>

        {/* Paired fields stack below sm. Two columns of a 375px viewport leave ~160px each, which
            a <input type="datetime-local"> below cannot render without clipping its own picker —
            and the platform/duration selects lose their labels' meaning at that width too. */}
        <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
          <Field label="Type" htmlFor="webinar-type">
            <select
              id="webinar-type"
              className="h-10 w-full rounded-sm border border-line-2 bg-surface px-3 text-sm text-ink outline-none focus:border-ink-3"
              {...register('type', { required: true })}
            >
              <option value="group">Group</option>
              <option value="live">Live</option>
            </select>
          </Field>

          <Field label="Duration (min)" htmlFor="webinar-duration" error={errors.duration_min?.message}>
            <Input
              id="webinar-duration"
              type="number"
              min={1}
              placeholder="60"
              {...register('duration_min', {
                setValueAs: (v) => (v === '' || v == null ? undefined : Number(v)),
                min: { value: 1, message: 'Must be at least 1' },
              })}
            />
          </Field>
        </div>

        <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
          <Field label="When" htmlFor="webinar-when" error={errors.scheduled_at?.message}>
            <Input
              id="webinar-when"
              type="datetime-local"
              {...register('scheduled_at', { required: 'Pick a date and time' })}
            />
          </Field>

          <Field label="Seats (optional)" htmlFor="webinar-capacity" error={errors.capacity?.message}>
            <Input
              id="webinar-capacity"
              type="number"
              min={1}
              placeholder="Unlimited"
              {...register('capacity', {
                setValueAs: (v) => (v === '' || v == null ? undefined : Number(v)),
                min: { value: 1, message: 'Must be at least 1' },
              })}
            />
          </Field>
        </div>

        <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
          <Field label="Platform" htmlFor="webinar-platform">
            <select
              id="webinar-platform"
              className="h-10 w-full rounded-sm border border-line-2 bg-surface px-3 text-sm text-ink outline-none focus:border-ink-3"
              {...register('platform')}
            >
              <option value="">Not decided yet</option>
              {PLATFORM_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Join link" htmlFor="webinar-join-url" error={errors.join_url?.message}>
            <Input
              id="webinar-join-url"
              type="url"
              placeholder="https://…"
              {...register('join_url', {
                // The API rejects anything else; catching it here saves a round-trip and points at
                // the field instead of raising a toast detached from the input that caused it.
                pattern: { value: /^https:\/\/\S+$/i, message: 'Must be an https:// link' },
                onBlur: (e: React.FocusEvent<HTMLInputElement>) => onJoinUrlBlur(e.target.value),
              })}
            />
          </Field>
        </div>

        <p className="-mt-1.5 text-[11px] text-muted">
          You can add the link later — attendees see it once you do.
        </p>

        {/* Speakers — free text, since a presenter is usually an external expert with no account. */}
        <div className="flex flex-col gap-2 border-t border-line pt-3">
          <div className="flex items-center justify-between gap-2">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-muted">Speakers</span>
            <button
              type="button"
              onClick={() => speakers.append({ name: '', title: '' })}
              disabled={speakers.fields.length >= MAX_SPEAKERS}
              className="inline-flex items-center gap-1 text-[11px] font-medium text-accent hover:text-accent-2 disabled:cursor-not-allowed disabled:text-muted"
            >
              <Icon.plus className="h-3 w-3" /> Add speaker
            </button>
          </div>

          {speakers.fields.length === 0 ? (
            <p className="text-[11px] text-muted">Nobody listed yet — add whoever is presenting.</p>
          ) : (
            speakers.fields.map((field, i) => (
              <div key={field.id} className="flex items-center gap-2">
                <Input
                  aria-label={`Speaker ${i + 1} name`}
                  placeholder="Full name"
                  className="flex-1"
                  {...register(`speakers.${i}.name` as const, { maxLength: 120 })}
                />
                <Input
                  aria-label={`Speaker ${i + 1} title`}
                  placeholder="Role (optional)"
                  className="flex-1"
                  {...register(`speakers.${i}.title` as const, { maxLength: 160 })}
                />
                <button
                  type="button"
                  aria-label={`Remove speaker ${i + 1}`}
                  onClick={() => speakers.remove(i)}
                  className="grid h-8 w-8 flex-none place-items-center rounded-btn text-muted transition-colors hover:text-danger"
                >
                  <Icon.close className="h-3.5 w-3.5" />
                </button>
              </div>
            ))
          )}
        </div>
      </form>
    </Modal>
  );
}
